import { useEffect } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import getTextColor from 'chat-ui/src/hooks/getTextColorContrast';
import { TranslationLanguage } from '@common/ts/types';
import { shuffleArray } from '@common/utils';
import { toast } from 'react-toastify';
import { ConversationStatus, LogoVariant, ScreeningState, ScreeningType } from '@/enums/enums';
import { ScreeningQuestionsScreenProps } from '@/types';
import ThankYouScreenScreeningQuestions from '../ThankYouScreenScreeningQuestions';
import useGoogleTranslate from '@/hooks/useGoogleTranslate';
import { SCREENING_QUESTIONS_TRANSLATION } from '@/constants/translations';
import useScreeningLocalStorage from '@/hooks/useScreeningLocalStorage';
import useScreeningState from '@/hooks/useScreeningState';
import ScreeningQuestionsProgressBar from './ScreeningQuestionsProgressBar';
import ScreeningQuestionProgressCount from './ScreeningQuestionProgressCount';
import ScreeningQuestion from './ScreeningQuestion';
import ScreeningQuestionStateButton from './ScreeningQuestionStateButton';
import SelectAnswer from './answers/SelectAnswer';
import NumericAnswer from './answers/NumericAnswer';
import useCompanyLogo from '@/hooks/useCompanyLogo';

function ScreeningQuestionsScreen({
  themeData,
  onScreenQuestionsEnd,
  screeningQuestions,
  projectStorageSuffix,
  projectId,
  isSandboxChat,
}: ScreeningQuestionsScreenProps) {
  const chatId = projectId?.slice(1);
  const basePath = isSandboxChat ? ConversationStatus.SANDBOX : ConversationStatus.PUBLISHED;

  const { logo, isCustomLogoPresent } = useCompanyLogo({
    themeData,
    variant: LogoVariant.WHITE,
    className: 'absolute bottom-5 right-5 z-0',
  });

  // Local storage hook
  const {
    storedConversationId,
    screeningQuestionIndex,
    disqualificationRedirectUrl,
    projectLanguageLabel,
    projectLanguageCode,
  } = useScreeningLocalStorage({ projectStorageSuffix });

  // Screening state hook
  const {
    initialAnswers,
    setInitialAnswers,
    currentQuestion,
    activeQuestionIndex,
    selectedAnswers,
    currentScreeningState,
    disqualificationLink,
    isLoading,
    submitScreeningAnswer,
    handleSelectAnswer,
    isPending,
  } = useScreeningState({
    screeningQuestions,
    initialQuestionIndex: screeningQuestionIndex,
    disqualificationRedirectUrl,
    projectStorageSuffix,
    projectLanguageLabel,
    basePath,
    projectId,
    storedConversationId,
    chatId,
    onScreenQuestionsEnd,
  });

  const languageCode = projectLanguageCode || 'en';
  const t = SCREENING_QUESTIONS_TRANSLATION[languageCode as TranslationLanguage];

  // Handle google translation
  const {
    screeningQuestion,
    screeningAnswers,
    isPending: isTranslating,
    error: errorTranslating,
    translateTextBatch,
  } = useGoogleTranslate({
    languageCode,
    initialAnswers,
    initialQuestion: currentQuestion.question,
  });

  // Show toast error for translation failures
  useEffect(() => {
    if (errorTranslating) {
      toast.error(t.errorTranslatingDescription);
    }
  }, [errorTranslating, t.errorTranslatingTitle, t.errorTranslatingDescription]);

  useEffect(() => {
    const { randomize, responses, question: currentQuestionText } = currentQuestion;
    const answers = randomize ? shuffleArray(responses) : responses;
    // These will be initial answers, which will be used for translation mapping
    setInitialAnswers(answers);
    // Example: ["Red", "Blue", "Green"]
    const answerValues = answers ? answers.map(({ value }) => value.trim()) : [];
    const generatedTextForTranslation = [currentQuestionText.trim(), ...answerValues].join(
      ' ~~S~~ ',
    );
    // Example: "What is your favorite color? ~~S~~ Red ~~S~~ Blue ~~S~~ Green"
    translateTextBatch(generatedTextForTranslation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, translateTextBatch]);

  // Deferred variables
  const progressCount: number | undefined = activeQuestionIndex + 1;
  const progressLimit: number | undefined = screeningQuestions.length;
  const percentage = progressCount && progressLimit ? (progressCount / progressLimit) * 100 : 0;
  const buttonText = activeQuestionIndex < screeningQuestions.length - 1 ? t.next : t.finish;
  const isButtonDisabled = selectedAnswers.length === 0 || isPending || isTranslating;
  const isDisqualified = currentScreeningState === ScreeningState.DISQUALIFIED;
  const thankYouScreenIsVisible = isDisqualified || disqualificationLink;
  const progressBarWidth = thankYouScreenIsVisible ? 0 : percentage ?? 0;
  const consentBackground = themeData?.['chat-consent-bg'] || '#FF6F98';

  return (
    <div
      style={{
        backgroundColor: consentBackground,
        color: getTextColor(consentBackground || '#FFFFFF'),
      }}
      className="screening-questions-container"
    >
      <div className="screening-questions-card">
        {/* progress bar */}
        <ScreeningQuestionsProgressBar
          percentage={percentage}
          backgroundColor={themeData?.primary || '#096846'}
          progressBarWidth={progressBarWidth}
        />

        {thankYouScreenIsVisible ? (
          <ThankYouScreenScreeningQuestions
            themeData={themeData}
            disqualificationLink={disqualificationLink}
          />
        ) : (
          <div className="relative" dir="auto">
            <ScreeningQuestionProgressCount
              progressCount={progressCount}
              progressLimit={progressLimit}
              t={t}
            />

            <ScreeningQuestion question={screeningQuestion} />

            {/* Answers */}
            {isTranslating ? (
              <div className="flex items-center justify-center w-full min-h-[108px]">
                <IconLoader2 className="animate-spin" size={30} color="#FF6F98" />
              </div>
            ) : (
              <div className="flex flex-col">
                {currentQuestion?.type === ScreeningType.NUMERIC ? (
                  <NumericAnswer
                    selectedAnswers={selectedAnswers}
                    handleSelectAnswer={handleSelectAnswer}
                    label={t.writeInNumbers}
                  />
                ) : (
                  <SelectAnswer
                    type={currentQuestion.type}
                    screeningAnswers={screeningAnswers}
                    selectedAnswers={selectedAnswers}
                    handleSelectAnswer={handleSelectAnswer}
                  />
                )}
              </div>
            )}
            <ScreeningQuestionStateButton
              isButtonDisabled={isButtonDisabled}
              submitScreeningAnswer={submitScreeningAnswer}
              backgroundColor={themeData?.primary || '#096846'}
              buttonText={buttonText}
              isLoading={isPending || isTranslating || isLoading}
            />
          </div>
        )}
      </div>
      {/* logo */}
      {!isCustomLogoPresent && logo}
    </div>
  );
}

export default ScreeningQuestionsScreen;
